import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ShieldAlert, Loader2, CheckCircle, AlertTriangle, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';

const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

const YEARS = ['all', String(new Date().getFullYear()), String(new Date().getFullYear() - 1)];

// Cross-checks every employee's "used"/"balance" against their approved leave records.
export const BalanceAuditDialog = ({ selectedEntity, onDone }) => {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [confirmFix, setConfirmFix] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year });
      if (selectedEntity?.entity_id) params.append('entity_id', selectedEntity.entity_id);
      const res = await fetch(`${API_URL}/api/leaves/balances/audit?${params}`, {
        headers: getHeaders(), credentials: 'include'
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Audit failed');
      setData(d);
      if (!d.rows.length) toast.success('Every leave balance matches the approved leave records');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const fixAll = async () => {
    setFixing(true);
    setConfirmFix(false);
    try {
      const res = await fetch(`${API_URL}/api/leaves/balances/audit/fix`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ year, entity_id: selectedEntity?.entity_id || null })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Fix failed');
      toast.success(d.message);
      await run();
      onDone && onDone();
    } catch (e) { toast.error(e.message); }
    finally { setFixing(false); }
  };

  const rows = data?.rows || [];
  const unconfigured = data?.unconfigured || [];

  return (
    <>
      <Button variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50"
        onClick={() => { setOpen(true); setData(null); }} data-testid="balance-audit-btn">
        <ShieldAlert className="w-4 h-4 mr-1.5" />Check Mismatches
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[95vw] lg:max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="balance-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" /> Leave Balance Health Check
            </DialogTitle>
            <DialogDescription>
              Compares every employee's recorded <b>Taken</b> and <b>Balance</b> against the days actually
              approved in their leave records, and lists staff who have no leave policy assigned.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Leave records of</label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-40" data-testid="audit-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEARS.map(y => <SelectItem key={y} value={y}>{y === 'all' ? 'All years' : y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={run} disabled={loading} data-testid="audit-run">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-1.5" />}
              Run Check
            </Button>
            {rows.some(r => !r.comp_off && !r.issue.startsWith('yearly quota')) && (
              <Button variant="destructive" onClick={() => setConfirmFix(true)} disabled={fixing} data-testid="audit-fix-all">
                {fixing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wrench className="w-4 h-4 mr-1.5" />}
                Fix All ({rows.filter(r => !r.comp_off && !r.issue.startsWith('yearly quota')).length})
              </Button>
            )}
            {data && (
              <span className="text-xs text-slate-500 self-center">
                Scope: {selectedEntity?.name || 'all institutes'}
              </span>
            )}
          </div>

          {data && (
            <div className="flex flex-wrap gap-2 text-xs" data-testid="audit-summary">
              <Badge variant="outline">{data.summary.employees} employees checked</Badge>
              <Badge variant="outline">{data.summary.approved_records} approved leave records</Badge>
              <Badge className={rows.length ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                {rows.length} mismatch{rows.length === 1 ? '' : 'es'}
              </Badge>
              {data.summary.unconfigured > 0 && (
                <Badge className="bg-amber-100 text-amber-800">{data.summary.unconfigured} staff without a leave policy</Badge>
              )}
            </div>
          )}

          {data && rows.length === 0 && (
            <div className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
              <CheckCircle className="w-4 h-4" /> All balances match the approved leave records.
            </div>
          )}

          {rows.length > 0 && (
            <div className="rounded-md border overflow-x-auto" data-testid="audit-rows">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead className="text-right">Quota</TableHead>
                    <TableHead className="text-right">Taken (recorded)</TableHead>
                    <TableHead className="text-right">Taken (actual)</TableHead>
                    <TableHead className="text-right">Balance now</TableHead>
                    <TableHead className="text-right">Should be</TableHead>
                    <TableHead>Problem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.employee_id}-${r.leave_type}-${i}`} data-testid={`audit-row-${r.employee_id}`}>
                      <TableCell className="font-medium">{r.employee_name || r.employee_id}</TableCell>
                      <TableCell className="text-xs text-slate-500">{r.department || '—'}</TableCell>
                      <TableCell>{r.leave_type}</TableCell>
                      <TableCell className="text-right">{r.total}</TableCell>
                      <TableCell className="text-right">{r.recorded_used}</TableCell>
                      <TableCell className="text-right font-semibold">{r.actual_used}</TableCell>
                      <TableCell className="text-right">{r.recorded_balance}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700">{r.expected_balance}</TableCell>
                      <TableCell className="text-xs">
                        {r.comp_off
                          ? <span className="text-amber-700">Comp-off — use the Comp-Off resync tool</span>
                          : r.issue}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {unconfigured.length > 0 && (
            <div className="space-y-2" data-testid="audit-unconfigured">
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <b>{unconfigured.length} staff have no leave policy assigned</b> — until a policy is applied they
                  have no quota, so approved leave has nothing to deduct from. Assign a policy from
                  Leave Policy → Employees, then run this check again.
                </div>
              </div>
              <div className="rounded-md border overflow-x-auto max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Policy</TableHead>
                      <TableHead>Balance rows</TableHead>
                      <TableHead className="text-right">Approved days on record</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unconfigured.map(u => (
                      <TableRow key={u.employee_id} data-testid={`audit-noplan-${u.employee_id}`}>
                        <TableCell className="font-medium">{u.employee_name || u.employee_id}</TableCell>
                        <TableCell className="text-xs text-slate-500">{u.department || '—'}</TableCell>
                        <TableCell>{u.has_policy ? <Badge variant="outline">Assigned</Badge> : <Badge className="bg-red-100 text-red-700">Missing</Badge>}</TableCell>
                        <TableCell>{u.has_balance_rows ? 'Yes' : <span className="text-red-600">None</span>}</TableCell>
                        <TableCell className="text-right">{u.approved_days}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={confirmFix} onOpenChange={setConfirmFix}>
        <DialogContent className="max-w-md" data-testid="audit-fix-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Rewrite {rows.filter(r => !r.comp_off && !r.issue.startsWith('yearly quota')).length} balance row(s)?
            </DialogTitle>
            <DialogDescription>
              For <b>{selectedEntity?.name || 'every institute'}</b> ({year === 'all' ? 'all years' : year}),
              "Taken" will be set to the days actually approved in the leave records and the balance
              recalculated. Comp-off rows and staff with no quota are left untouched.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmFix(false)} data-testid="audit-fix-cancel">Cancel</Button>
            <Button variant="destructive" onClick={fixAll} data-testid="audit-fix-confirm-btn">
              <Wrench className="w-4 h-4 mr-1.5" /> Fix balances
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
